/**
 * Workspaces
 *
 * Shared data helpers for workspace creation, lookup, and updates.
 *
 * Primary responsibilities:
 * - Persist workspace rows through browser API or Supabase clients
 * - Resolve accessible workspaces and selected workspace defaults
 *
 * Dependencies:
 * - Supabase service client
 */

import { Tables, TablesInsert, TablesUpdate } from '@/supabase/types'

import { createServiceClient } from '@/lib/supabase/service-client'

type SupabaseLikeClient = ReturnType<typeof createServiceClient>
type WorkspaceTemplate = Tables<'workspaces'> | null | undefined
type AuthenticatedFetch = typeof fetch

const DEFAULT_WORKSPACE_MODEL = 'losi-omega'
const DEFAULT_WORKSPACE_PROMPT = 'You are a friendly, helpful AI assistant.'
const DEFAULT_WORKSPACE_TEMPERATURE = 0.5
const DEFAULT_WORKSPACE_CONTEXT_LENGTH = 4096

const getDefaultClient = (client?: SupabaseLikeClient) => {
  if (client) return client

  // In the browser, default to the browser client to avoid requiring service
  // credentials client-side.
  if (typeof window !== 'undefined') {
    const { supabase } = require('@/lib/supabase/browser-client') as {
      supabase: SupabaseLikeClient
    }
    return supabase
  }

  return createServiceClient()
}

const isBrowser = () => typeof window !== 'undefined'

const getAuthenticatedFetch = (): AuthenticatedFetch => {
  const mod = require('@/lib/auth/authenticated-fetch') as {
    authenticatedFetch: AuthenticatedFetch
  }
  return mod.authenticatedFetch
}

const getResponseError = async (response: Response, fallback: string) => {
  const text = await response.text().catch(() => '')
  return text.trim() || fallback
}

/**
 * Workspace type discriminator (`workspaces.kind`).
 *
 * - `personal`: main Losi workspace (default for every existing flow)
 * - `home`: Losi Home household workspace, created explicitly only
 *
 * This is independent of `is_home`, which already means "personal
 * default workspace" (see `getHomeWorkspaceByUserId` below and
 * `buildBootstrapHomeWorkspaceInsert` in lib/auth/user-bootstrap.ts).
 * A `kind: 'home'` workspace must never be forced `is_home: true`.
 */
export type WorkspaceKind = 'personal' | 'home'

export const normalizeWorkspaceKind = (value: unknown): WorkspaceKind =>
  value === 'home' ? 'home' : 'personal'

export const buildWorkspaceInsert = ({
  ownerId,
  name,
  description,
  kind,
  template
}: {
  ownerId: string
  name: string
  description?: string | null
  kind?: WorkspaceKind | string | null
  template?: WorkspaceTemplate
}): TablesInsert<'workspaces'> => ({
  user_id: ownerId,
  name,
  description: description?.trim() || '',
  default_context_length:
    template?.default_context_length ?? DEFAULT_WORKSPACE_CONTEXT_LENGTH,
  default_model: template?.default_model ?? DEFAULT_WORKSPACE_MODEL,
  default_prompt: template?.default_prompt ?? DEFAULT_WORKSPACE_PROMPT,
  default_temperature:
    template?.default_temperature ?? DEFAULT_WORKSPACE_TEMPERATURE,
  embeddings_provider: template?.embeddings_provider ?? 'openai',
  include_profile_context: template?.include_profile_context ?? true,
  include_workspace_instructions:
    template?.include_workspace_instructions ?? true,
  instructions: template?.instructions ?? '',
  // kind is explicit-only: it is never inherited from the template, so
  // cloning/creating from a Losi Home workspace cannot silently create
  // another Home. Callers opt in with kind: 'home'.
  kind: normalizeWorkspaceKind(kind),
  is_home: false,
  sharing: template?.sharing ?? 'private'
})

export const getHomeWorkspaceByUserId = async (userId: string, client?: SupabaseLikeClient) => {
  if (!client && isBrowser()) {
    const workspaces = await getWorkspacesByUserId(userId)
    const homeWorkspace = workspaces.find((workspace) => workspace.is_home)

    if (!homeWorkspace) {
      throw new Error('Workspace operation failed')
    }

    return homeWorkspace.id
  }

  const supabase = getDefaultClient(client)
  const { data: homeWorkspace, error } = await supabase
    .from('workspaces')
    .select('*')
    .eq('user_id', userId)
    .eq('is_home', true)
    .single()

  if (!homeWorkspace) {
    throw new Error(error?.message || 'Workspace operation failed')
  }

  return homeWorkspace.id
}

export const getWorkspaceById = async (
  workspaceId: string,
  client?: SupabaseLikeClient
): Promise<Record<string, unknown>> => {
  if (!client && isBrowser()) {
    const response = await getAuthenticatedFetch()(
      `/api/workspaces/${workspaceId}`,
      { cache: 'no-store' }
    )

    if (!response.ok) {
      throw new Error(await getResponseError(response, 'Workspace operation failed'))
    }

    return (await response.json()) as Record<string, unknown>
  }

  const supabase = getDefaultClient(client)

  // Avoid deep type instantiation by keeping this query shallow.
  const { data: workspace, error } = await supabase
    .from('workspaces')
    .select('*')
    .eq('id', workspaceId)
    .single()

  if (!workspace) {
    throw new Error(error?.message || 'Workspace operation failed')
  }

  return workspace as unknown as Record<string, unknown>
}

export const getWorkspacesByUserId = async (userId: string, client?: SupabaseLikeClient) => {
  if (!client && isBrowser()) {
    const response = await getAuthenticatedFetch()('/api/workspaces', {
      cache: 'no-store'
    })

    if (!response.ok) {
      throw new Error(await getResponseError(response, 'Workspace operation failed'))
    }

    return (await response.json()) as Tables<'workspaces'>[]
  }

  const supabase = getDefaultClient(client)

  const { data: ownedWorkspaces, error } = await supabase
    .from('workspaces')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (!ownedWorkspaces) {
    throw new Error(error?.message || 'Workspace operation failed')
  }

  const ownedWorkspacesWithRole = ownedWorkspaces.map((workspace) => ({
    ...workspace,
    access_role: 'owner'
  }))

  const { data: memberships, error: memberError } = await supabase
    .from('workspace_members')
    .select('workspace_id, role')
    .eq('user_id', userId)

  if (memberError || !memberships?.length) {
    return ownedWorkspacesWithRole
  }

  const roleByWorkspaceId = new Map(
    memberships.map((membership) => [
      membership.workspace_id,
      membership.role
    ])
  )

  const ownedIds = new Set(ownedWorkspaces.map((workspace: { id: string }) => workspace.id))
  const memberOnlyIds = [...new Set(memberships.map((membership) => membership.workspace_id))]
    .filter((workspaceId) => !ownedIds.has(workspaceId))

  if (memberOnlyIds.length === 0) {
    return ownedWorkspacesWithRole
  }

  const { data: memberWorkspaces, error: memberWorkspacesError } = await supabase
    .from('workspaces')
    .select('*')
    .in('id', memberOnlyIds)

  if (memberWorkspacesError || !memberWorkspaces) {
    return ownedWorkspacesWithRole
  }

  const allWorkspaces = [
    ...ownedWorkspacesWithRole,
    ...memberWorkspaces.map((workspace) => ({
      ...workspace,
      access_role: roleByWorkspaceId.get(workspace.id) ?? 'member'
    }))
  ]
  allWorkspaces.sort(
    (a: { created_at?: string | null }, b: { created_at?: string | null }) =>
      new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()
  )

  return allWorkspaces
}

/**
 * Returns a default workspace id for the user (home workspace or first accessible).
 * Use when a tool needs workspace_id but the caller did not pass it (e.g. chat/Sidecar).
 * Does not throw; returns null if user has no workspace.
 */
export const getDefaultWorkspaceIdForUser = async (
  userId: string,
  client?: SupabaseLikeClient
): Promise<string | null> => {
  try {
    return await getHomeWorkspaceByUserId(userId, client)
  } catch {
    try {
      const workspaces = await getWorkspacesByUserId(userId, client)
      return workspaces?.length ? (workspaces[0] as { id: string }).id : null
    } catch {
      return null
    }
  }
}

/**
 * Returns all workspaces the user has access to: owned (workspaces.user_id)
 * plus workspaces where the user is in workspace_members. Use this when the
 * list should match what RLS allows (e.g. Nexus agents from all accessible
 * workspaces). getWorkspacesByUserId returns only owned workspaces.
 */
export const getWorkspacesWithAccess = async (userId: string, client?: SupabaseLikeClient) => {
  if (!client && isBrowser()) {
    return getWorkspacesByUserId(userId)
  }

  const supabase = getDefaultClient(client)

  const owned = await getWorkspacesByUserId(userId, supabase)
  const { data: memberships, error: memberError } = await supabase
    .from('workspace_members')
    .select('workspace_id')
    .eq('user_id', userId)

  if (memberError || !memberships?.length) {
    return owned
  }

  const memberIds = [...new Set(memberships.map((m: { workspace_id: string }) => m.workspace_id))]
  const ownedIds = new Set(owned.map((w: { id: string }) => w.id))
  const onlyMemberIds = memberIds.filter((id) => !ownedIds.has(id))
  if (onlyMemberIds.length === 0) {
    return owned
  }

  const { data: memberWorkspaces, error: wsError } = await supabase
    .from('workspaces')
    .select('*')
    .in('id', onlyMemberIds)
    .order('created_at', { ascending: false })

  if (wsError || !memberWorkspaces?.length) {
    return owned
  }

  const combined = [...owned, ...memberWorkspaces]
  combined.sort(
    (a: { created_at?: string | null }, b: { created_at?: string | null }) =>
      new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()
  )
  return combined
}

export const createWorkspace = async (
  workspace: TablesInsert<'workspaces'>,
  timezone?: string,
  client?: SupabaseLikeClient
) => {
  if (!client && isBrowser()) {
    const response = await getAuthenticatedFetch()('/api/workspaces', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ workspace, timezone })
    })

    if (!response.ok) {
      throw new Error(await getResponseError(response, 'Workspace operation failed'))
    }

    return (await response.json()) as Tables<'workspaces'>
  }

  const supabase = getDefaultClient(client)

  const { data: createdWorkspace, error } = await supabase
    .from('workspaces')
    .insert([
      {
        ...workspace,
        timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    ])
    .select('*')
    .single()

  if (error) {
    throw new Error(error?.message || 'Workspace operation failed')
  }

  return createdWorkspace
}

export const updateWorkspace = async (
  workspaceId: string,
  workspace: TablesUpdate<'workspaces'>,
  client?: SupabaseLikeClient
) => {
  if (!client && isBrowser()) {
    const response = await getAuthenticatedFetch()(
      `/api/workspaces/${workspaceId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(workspace)
      }
    )

    if (!response.ok) {
      throw new Error(await getResponseError(response, 'Workspace operation failed'))
    }

    return (await response.json()) as Tables<'workspaces'>
  }

  const supabase = getDefaultClient(client)

  const { data: updatedWorkspace, error } = await supabase
    .from('workspaces')
    .update(workspace)
    .eq('id', workspaceId)
    .select('*')
    .single()

  if (error) {
    throw new Error(error?.message || 'Workspace operation failed')
  }

  return updatedWorkspace
}

export const deleteWorkspace = async (workspaceId: string, client?: SupabaseLikeClient) => {
  if (!client && isBrowser()) {
    const response = await getAuthenticatedFetch()(
      `/api/workspaces/${workspaceId}`,
      { method: 'DELETE' }
    )

    if (!response.ok) {
      throw new Error(await getResponseError(response, 'Workspace operation failed'))
    }

    return true
  }

  const supabase = getDefaultClient(client)

  const { error } = await supabase.from('workspaces').delete().eq('id', workspaceId)

  if (error) {
    throw new Error(error?.message || 'Workspace operation failed')
  }

  return true
}
